import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { accountsApi, type DiscoverConfig } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

export function SetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'creds' | 'config' | 'connecting' | 'done'>('creds');
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [config, setConfig] = useState<DiscoverConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Override avanzati
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  const [acceptSelfSignedCert, setAcceptSelfSignedCert] = useState(false);

  async function handleDiscover(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { config } = await accountsApi.discover(emailAddress);
      setConfig(config);
      setImapHost(config.imap.host);
      setImapPort(config.imap.port);
      setSmtpHost(config.smtp.host);
      setSmtpPort(config.smtp.port);
      setStep('config');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Discovery fallita');
    } finally {
      setLoading(false);
    }
  }

  async function handleConnect() {
    setError(null);
    setStep('connecting');
    try {
      await accountsApi.add({
        emailAddress,
        password,
        displayName: displayName || undefined,
        imapHost: showAdvanced ? imapHost : undefined,
        imapPort: showAdvanced ? imapPort : undefined,
        smtpHost: showAdvanced ? smtpHost : undefined,
        smtpPort: showAdvanced ? smtpPort : undefined,
        imapTlsRejectUnauthorized: !acceptSelfSignedCert,
        smtpTlsRejectUnauthorized: !acceptSelfSignedCert,
      });
      setStep('done');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Connessione fallita';
      const isCertError =
        msg.toLowerCase().includes('certificate') ||
        msg.toLowerCase().includes('altname') ||
        msg.toLowerCase().includes('self signed');
      if (isCertError) {
        setError(
          msg + ' — Apri "Opzioni avanzate" qui sotto e attiva "Accetta certificato non verificato" per provare di nuovo.'
        );
        setShowAdvanced(true);
      } else {
        setError(msg);
      }
      setStep('config');
    }
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Mail className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>Collega il tuo account email</CardTitle>
              <CardDescription>
                Le tue credenziali vengono cifrate con AES-256-GCM. Solo tu puoi accedere alla tua casella.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {step === 'creds' && (
            <form onSubmit={handleDiscover} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="emailAddr">Indirizzo email</Label>
                <Input id="emailAddr" type="email" placeholder="info@tuaazienda.it" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} required autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emailPwd">Password email</Label>
                <Input id="emailPwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Nome visualizzato <span className="text-muted-foreground font-normal">(opzionale)</span></Label>
                <Input id="displayName" type="text" placeholder="Es. Ufficio commerciale" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading || !emailAddress || !password}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Continua
              </Button>
            </form>
          )}

          {step === 'config' && config && (
            <div className="space-y-4">
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">Configurazione rilevata</div>
                  <Badge variant={config.source === 'hardcoded' ? 'success' : 'secondary'}>
                    {config.source === 'hardcoded' ? 'Verificato' : 'Auto-rilevato'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">IMAP (in entrata)</div>
                    <div className="font-mono">{showAdvanced ? `${imapHost}:${imapPort}` : `${config.imap.host}:${config.imap.port}`}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">SMTP (in uscita)</div>
                    <div className="font-mono">{showAdvanced ? `${smtpHost}:${smtpPort}` : `${config.smtp.host}:${config.smtp.port}`}</div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Opzioni avanzate
              </button>

              {showAdvanced && (
                <div className="rounded-lg border p-4 space-y-4 bg-muted/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="imapHost">Host IMAP</Label>
                      <Input id="imapHost" value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="imapPort">Porta IMAP</Label>
                      <Input id="imapPort" type="number" value={imapPort} onChange={(e) => setImapPort(parseInt(e.target.value, 10) || 993)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="smtpHost">Host SMTP</Label>
                      <Input id="smtpHost" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="smtpPort">Porta SMTP</Label>
                      <Input id="smtpPort" type="number" value={smtpPort} onChange={(e) => setSmtpPort(parseInt(e.target.value, 10) || 465)} />
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptSelfSignedCert}
                        onChange={(e) => setAcceptSelfSignedCert(e.target.checked)}
                        className="mt-1"
                      />
                      <div className="text-sm">
                        <div className="font-medium">Accetta certificato non verificato</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Attivalo se il provider hosting (CloudPanel, Plesk, ecc.) usa un certificato condiviso non corrispondente al dominio email. La connessione resta cifrata in TLS, ma non viene verificata l'identita del certificato.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setStep('creds'); setError(null); }} className="flex-1">Indietro</Button>
                <Button onClick={handleConnect} className="flex-1">Connetti</Button>
              </div>
            </div>
          )}

          {step === 'connecting' && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
              <div>
                <div className="font-medium">Connessione in corso...</div>
                <div className="text-sm text-muted-foreground mt-1">Test IMAP e SMTP</div>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="py-12 text-center space-y-4">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <div>
                <div className="font-medium">Account collegato!</div>
                <div className="text-sm text-muted-foreground mt-1">Sincronizzazione iniziale in corso. Reindirizzamento alla inbox...</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
